import React, { useId, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Building2, FilePlus2, Globe, Loader2, User, X } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '../ui/dialog';
import { Switch } from '../ui/switch';
import { errorText } from '../../lib/apiErrors';
import { invalidateCustomFields } from '../../hooks/useCustomFields';
import { IMPORT_CREATABLE_TYPES, labelKey, labelToKey } from '../custom-fields/fieldTypes';

/**
 * "+ Add custom field" from the wizard's Map Fields step (#972): a small form
 * that creates a GLOBAL Contact or Company field through the admin
 * custom-fields API, without leaving the wizard.
 *
 * - Prefills the label from the CSV header, guesses Contact/Company, type Text,
 *   and (Dropdown) the options from the column's distinct values.
 * - Only the nine types the import can stage are offered (no Multi-select).
 * - Label clashes are caught instantly, with the same rules the server applies
 *   (`labelKey` mirrors `_label_key`); the server stays the authority, and a
 *   400 it answers with is shown inline. Nothing is created in either case.
 * - On success the custom-field cache is invalidated and `onCreated(field,
 *   entity)` is awaited (the wizard refetches the catalog and maps the column).
 * - `onCloseAutoFocus` lets the wizard hand focus back to the column's trigger.
 */

// A key is made from A-Z and 0-9 only (labelToKey), so a label of other letters has none.
export const EMPTY_KEY_MESSAGE = 'Use at least one letter A-Z or number in the label so a key can be made.';

// The server's 400 when the name belongs to an ARCHIVED field: it still reserves the name, and
// the only way out is to restore that field (or choose another name) in Admin → Custom Fields.
export const ARCHIVED_CLASH = /is archived and still reserves the name/i;

const COMPANY_GUESS = /company|account|fund|industry|revenue|employee|domain|website/i;
const ENTITIES = [
    { value: 'contact', label: 'Contact', noun: 'contact', plural: 'contacts', Icon: User },
    { value: 'account', label: 'Company', noun: 'company', plural: 'companies', Icon: Building2 },
];

/** `referral_source` → "Referral Source". */
export function humanizeHeader(header) {
    const words = String(header || '')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        // A digit before a capital is a word break only when a word follows ("web2Lead"), never in "E2E".
        .replace(/([0-9])([A-Z][a-z])/g, '$1 $2')
        .replace(/[_\-.]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    return words.replace(/(^|\s)(\p{Ll})/gu, (_m, space, ch) => space + ch.toUpperCase()) || String(header || '');
}

const distinctOptions = (stats) => {
    const seen = new Set();
    return (stats?.distinct || []).map((v) => String(v ?? '').trim()).filter((v) => {
        if (!v || seen.has(v)) return false;
        seen.add(v);
        return true;
    });
};

function Chip({ checked, children, ...rest }) {
    return (
        <label className="relative inline-flex">
            <input type="radio" className="peer sr-only" checked={checked} {...rest} />
            <span className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border border-border bg-background text-[12.5px] font-medium text-foreground cursor-pointer select-none hover:bg-accent peer-checked:border-foreground peer-checked:bg-muted peer-focus-visible:ring-2 peer-focus-visible:ring-ring peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-background">
                {children}
            </span>
        </label>
    );
}

function Body({ header, stats, catalog, usedBy, api, importId, onCreated, onMapExisting, onClose, busyRef }) {
    const ids = { label: useId(), hint: useId(), error: useId(), required: useId(), requiredText: useId(), requiredHelp: useId(), optionsError: useId() };
    const [label, setLabel] = useState(() => humanizeHeader(header));
    const [entity, setEntity] = useState(() => (COMPANY_GUESS.test(header || '') ? 'account' : 'contact'));
    const [type, setType] = useState('text');
    const [options, setOptions] = useState(() => distinctOptions(stats));
    const [optionInput, setOptionInput] = useState('');
    const [required, setRequired] = useState(false);
    const [touched, setTouched] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [serverError, setServerError] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    const meta = ENTITIES.find((e) => e.value === entity);
    const trimmed = label.trim();
    const key = labelToKey(trimmed);

    // The same rules the server applies, against the catalog already on screen.
    const clash = useMemo(() => {
        if (!trimmed) return null;
        const list = (entity === 'account' ? catalog?.company_fields : catalog?.contact_fields) || [];
        const wanted = labelKey(trimmed);
        const builtin = list.find((f) => !f.is_custom && labelKey(f.label) === wanted);
        if (builtin) {
            const holder = usedBy?.[builtin.key];
            const taken = !!holder && holder !== header;
            return {
                kind: 'builtin', field: builtin, taken,
                message: taken
                    ? `“${builtin.label}” is already a ${meta.label} field, and ${holder} is mapped to it. Choose another name.`
                    : `“${builtin.label}” is already a ${meta.label} field. Choose another name, or map this column to it.`,
            };
        }
        const existing = list.find((f) => f.is_custom && (labelKey(f.label) === wanted || (key && f.key === `cf_${key}`)));
        if (existing) {
            return { kind: 'custom', field: existing, message: `A custom ${meta.noun} field called “${existing.label}” already exists.` };
        }
        if (!key) return { kind: 'empty-key', message: EMPTY_KEY_MESSAGE };
        return null;
    }, [trimmed, entity, key, catalog, usedBy, header, meta]);

    // The same key held by a custom field of the OTHER entity is allowed (the field lists
    // are per entity), but a column mapped to that key fills both: say so, never block.
    const sharedKey = useMemo(() => {
        if (!key || clash) return null;
        const other = ENTITIES.find((e) => e.value !== entity);
        const list = (other.value === 'account' ? catalog?.company_fields : catalog?.contact_fields) || [];
        return list.some((f) => f.is_custom && f.key === `cf_${key}`) ? other : null;
    }, [key, clash, entity, catalog]);

    // A Dropdown with no options could never be filled, and when Required it would block every save.
    const optionsMissing = type === 'select' && options.length === 0;
    const emptyMessage = touched && !trimmed ? 'Enter a label for the field.' : null;
    const problem = emptyMessage || clash?.message || serverError;

    const archivedClash = Boolean(serverError) && ARCHIVED_CLASH.test(serverError);

    const edit = (setter) => (value) => { setter(value); setServerError(null); };

    const addOption = () => {
        const v = optionInput.trim();
        if (v && !options.includes(v)) setOptions((o) => [...o, v]);
        setOptionInput('');
    };

    const submit = async (e) => {
        e.preventDefault();
        setTouched(true);
        setSubmitted(true);
        if (!trimmed || clash || optionsMissing || submitting) return;
        setSubmitting(true);
        busyRef.current = true;
        setServerError(null);
        let created;
        try {
            created = await api.createCustomField({
                label: trimmed, entity, field_type: type, required,
                options: type === 'select' ? options : [],
                source: 'list_management_import', import_id: importId,
            });
        } catch (err) {
            setServerError(errorText(err, 'The custom field could not be created.'));
            setSubmitting(false);
            busyRef.current = false;
            return;
        }
        invalidateCustomFields(entity);
        try {
            await onCreated(created, entity);
        } finally {
            setSubmitting(false);
            busyRef.current = false;
        }
    };

    return (
        <form onSubmit={submit} noValidate className="grid gap-4" data-testid="lm-add-field-form">
            <div className="flex items-start gap-3 pr-8">
                <span className="shrink-0 w-9 h-9 rounded-[10px] inline-flex items-center justify-center bg-muted text-foreground">
                    <FilePlus2 className="w-[17px] h-[17px]" aria-hidden="true" />
                </span>
                <div className="min-w-0">
                    <DialogTitle className="text-[17px] leading-tight">Add custom field</DialogTitle>
                    <DialogDescription className="mt-1 text-[12.5px]">
                        For CSV column <code className="font-mono text-foreground">{header}</code>
                    </DialogDescription>
                </div>
            </div>

            <div>
                <label htmlFor={ids.label} className="block text-[11px] font-semibold tracking-[0.06em] text-muted-foreground mb-1.5">LABEL *</label>
                <input
                    id={ids.label} data-testid="lm-add-field-label" value={label} autoComplete="off"
                    onChange={(e) => { edit(setLabel)(e.target.value); setTouched(true); }}
                    aria-invalid={problem ? true : undefined} aria-describedby={`${ids.hint}${problem ? ` ${ids.error}` : ''}`}
                    className={`w-full h-9 px-3 rounded-lg border bg-background text-[13px] text-foreground outline-none focus:ring-2 focus:ring-ring ${problem ? 'border-destructive' : 'border-border'}`} />
                <div id={ids.hint} className="mt-1.5 text-[12px] text-muted-foreground" data-testid="lm-add-field-key">
                    {key ? <>Key: <code className="font-mono">cf_{key}</code></> : 'The key is made from the label.'}
                </div>
                {sharedKey && (
                    <div role="status" data-testid="lm-add-field-shared-key" className="mt-1.5 text-[12px] text-muted-foreground">
                        A {sharedKey.label} field with this key exists; a column mapped to it fills both.
                    </div>
                )}
                {problem && (
                    <div id={ids.error} role="alert" data-testid="lm-add-field-error"
                         className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-[12.5px] text-destructive">
                        <span>{problem}</span>
                        {clash?.kind === 'builtin' && !clash.taken && !serverError && !emptyMessage && (
                            <button type="button" data-testid="lm-add-field-map-existing"
                                    onClick={() => onMapExisting(clash.field.key)}
                                    className="font-semibold underline underline-offset-2">
                                → Map to {clash.field.label} instead
                            </button>
                        )}
                        {archivedClash && (
                            // A new tab, so the wizard (its file, selection and mapping) is not lost.
                            <Link to="/admin/custom-fields" target="_blank" rel="noopener noreferrer"
                                  data-testid="lm-add-field-open-custom-fields"
                                  className="font-semibold underline underline-offset-2">
                                Open Custom Fields<span className="sr-only"> (opens in a new tab)</span>
                            </Link>
                        )}
                    </div>
                )}
            </div>

            <fieldset>
                <legend className="text-[11px] font-semibold tracking-[0.06em] text-muted-foreground mb-1.5">APPLIES TO</legend>
                <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Applies to">
                    {ENTITIES.map(({ value, label: text, Icon }) => (
                        <Chip key={value} name="lm-add-field-entity" value={value} checked={entity === value}
                              onChange={() => edit(setEntity)(value)} data-testid={`lm-add-field-entity-${value}`}>
                            <Icon className="w-3.5 h-3.5" aria-hidden="true" />{text}
                        </Chip>
                    ))}
                </div>
            </fieldset>

            <fieldset>
                <legend className="text-[11px] font-semibold tracking-[0.06em] text-muted-foreground mb-1.5">FIELD TYPE</legend>
                <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Field type">
                    {IMPORT_CREATABLE_TYPES.map((t) => (
                        <Chip key={t.value} name="lm-add-field-type" value={t.value} checked={type === t.value}
                              onChange={() => edit(setType)(t.value)} data-testid={`lm-add-field-type-${t.value}`}>
                            {t.label}
                        </Chip>
                    ))}
                </div>
            </fieldset>

            {type === 'select' && (
                <div data-testid="lm-add-field-options">
                    <div className="text-[11px] font-semibold tracking-[0.06em] text-muted-foreground mb-1.5">OPTIONS (DROPDOWN)</div>
                    <div className="flex flex-wrap items-center gap-1.5">
                        {options.map((o) => (
                            <span key={o} className="inline-flex items-center gap-1 h-7 pl-2.5 pr-1 rounded-md bg-muted text-[12.5px] text-foreground">
                                {o}
                                <button type="button" aria-label={`Remove option ${o}`} onClick={() => setOptions((all) => all.filter((x) => x !== o))}
                                        className="w-5 h-5 inline-flex items-center justify-center rounded text-muted-foreground hover:text-foreground hover:bg-accent">
                                    <X className="w-3 h-3" aria-hidden="true" />
                                </button>
                            </span>
                        ))}
                        <input
                            value={optionInput} onChange={(e) => setOptionInput(e.target.value)} aria-label="New option"
                            aria-invalid={optionsMissing && submitted ? true : undefined}
                            aria-describedby={optionsMissing ? ids.optionsError : undefined}
                            data-testid="lm-add-field-option-input" placeholder="Add an option"
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addOption(); } }}
                            className="h-7 w-[140px] px-2 rounded-md border border-border bg-background text-[12.5px] text-foreground outline-none placeholder:text-muted-foreground focus:ring-2 focus:ring-ring" />
                        <button type="button" onClick={addOption} data-testid="lm-add-field-option-add"
                                className="h-7 px-2.5 rounded-md border border-border text-[12.5px] font-medium text-foreground hover:bg-accent">
                            + Add option
                        </button>
                    </div>
                    {optionsMissing && (
                        <div id={ids.optionsError} role={submitted ? 'alert' : undefined} data-testid="lm-add-field-options-error"
                             className={`mt-2 text-[12.5px] ${submitted ? 'text-destructive' : 'text-muted-foreground'}`}>
                            A Dropdown needs at least one option.
                        </div>
                    )}
                    <p className="mt-1.5 text-[12px] text-muted-foreground">
                        {stats?.distinct?.length ? `Prefilled from the distinct values in ${header}. ` : ''}Remove any you don’t want; admins can add more later.
                        {stats?.distinct_capped && <span data-testid="lm-add-field-options-capped"> Only the first 20 distinct values are listed.</span>}
                    </p>
                </div>
            )}

            <div className="flex items-start gap-3">
                <Switch id={ids.required} checked={required} onCheckedChange={edit(setRequired)}
                        aria-labelledby={ids.requiredText} aria-describedby={ids.requiredHelp}
                        data-testid="lm-add-field-required"
                        className="mt-0.5 data-[state=unchecked]:bg-muted-foreground/60" />
                <div className="text-[12.5px]">
                    <div id={ids.requiredText} className="font-medium text-foreground">Required</div>
                    <div id={ids.requiredHelp} className="text-muted-foreground">
                        Required when someone creates or edits a record in the app. Existing and imported records can stay empty; the preview counts imported rows left empty.
                    </div>
                </div>
            </div>

            <div className="flex items-start gap-2 rounded-lg bg-muted px-3 py-2.5 text-[12.5px] text-muted-foreground">
                <Globe className="w-3.5 h-3.5 mt-0.5 shrink-0" aria-hidden="true" />
                <span>
                    Adds “{trimmed || 'this field'}” to every {meta.noun} in this workspace. Existing {meta.plural} start empty until a value is imported or entered.
                </span>
            </div>

            <div className="flex flex-wrap items-center gap-3 pt-3 border-t border-border">
                <span className="flex-1 min-w-[160px] text-[12px] text-muted-foreground">Also listed in Admin → Custom Fields</span>
                <button type="button" onClick={onClose} disabled={submitting} data-testid="lm-add-field-cancel"
                        className="h-9 px-4 rounded-lg border border-border bg-background text-[13px] font-medium text-foreground hover:bg-accent disabled:opacity-50">
                    Cancel
                </button>
                <button type="submit" disabled={submitting} data-testid="lm-add-field-submit"
                        className="inline-flex items-center gap-2 h-9 px-4 rounded-lg bg-primary text-primary-foreground text-[13px] font-semibold hover:opacity-90 disabled:opacity-60">
                    {submitting && <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />}Create field
                </button>
            </div>
        </form>
    );
}

export default function LmAddCustomFieldDialog({
    open, header, stats, catalog, usedBy, api, importId, onCreated, onMapExisting, onClose, onCloseAutoFocus,
}) {
    // A create in flight must finish (and map the column) before the dialog may close.
    const busyRef = useRef(false);
    return (
        <Dialog open={open && !!header} onOpenChange={(v) => { if (!v && !busyRef.current) onClose(); }}>
            <DialogContent
                data-testid="lm-add-field-dialog"
                className="sm:max-w-[560px] w-[calc(100vw-32px)]"
                onCloseAutoFocus={(e) => {
                    if (onCloseAutoFocus) { e.preventDefault(); onCloseAutoFocus(); }
                }}>
                {header && (
                    <Body
                        key={header} header={header} stats={stats} catalog={catalog} usedBy={usedBy} api={api}
                        importId={importId} onCreated={onCreated} onMapExisting={onMapExisting} onClose={onClose} busyRef={busyRef} />
                )}
            </DialogContent>
        </Dialog>
    );
}
